import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    LinearProgress,
    Link,
    Paper,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';
import {useNavigate} from 'react-router';
import {fetchRequirementsOverview} from '../../firebase/firestore/icr';
import {coverageScoreColor} from './coverage/coverageScoreColor';
import RequirementsOwnerTagsFilters from './RequirementsOwnerTagsFilters';
import {TYPE_COLORS} from './similarity/similarityLabels';

const BUCKET_KEYS = ['covered', 'partial', 'not_covered', 'unknown'];
const BUCKET_LABELS = {
    covered: 'Covered',
    partial: 'Partial',
    not_covered: 'Not covered',
    unknown: 'Unknown',
};
const BUCKET_COLORS = {
    covered: '#16A34A',
    partial: '#CA8A04',
    not_covered: '#DC2626',
    unknown: '#9E9E9E',
};

const CT_STATUS_ORDER = [
    'planned',
    'assigned',
    'in_progress',
    'paused',
    'failed',
    'complete',
    'cancelled',
];
const CT_STATUS_COLORS = {
    planned: '#90CAF9',
    assigned: '#64B5F6',
    in_progress: '#42A5F5',
    paused: '#FFB74D',
    failed: '#EF5350',
    complete: '#66BB6A',
    cancelled: '#BDBDBD',
};

const CT_PRIORITY_ORDER = ['Low', 'Medium', 'High', 'Routine'];

const TOP_N = 8;

function emptyBuckets() {
    return {covered: 0, partial: 0, not_covered: 0, unknown: 0};
}

function normalizeBuckets(raw) {
    const base = emptyBuckets();
    if (!raw || typeof raw !== 'object') return base;
    for (const key of BUCKET_KEYS) {
        const n = Number(raw[key]);
        base[key] = Number.isFinite(n) ? n : 0;
    }
    return base;
}

function WidgetCard({title, children, sx}) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 1.5,
                height: '100%',
                minWidth: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                ...sx,
            }}
        >
            {title && (
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                    {title}
                </Typography>
            )}
            {children}
        </Paper>
    );
}

function KpiCard({label, value, onClick, color, tooltip}) {
    const card = (
        <Paper
            variant="outlined"
            component={onClick ? 'button' : 'div'}
            type={onClick ? 'button' : undefined}
            onClick={onClick}
            sx={{
                p: 1.75,
                width: '100%',
                textAlign: 'left',
                cursor: onClick ? 'pointer' : 'default',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                borderRadius: 1,
                '&:hover': onClick ? {borderColor: 'primary.main', bgcolor: 'action.hover'} : undefined,
            }}
        >
            <Typography
                variant="h4"
                fontWeight={700}
                fontFamily='"Roboto Mono", monospace'
                sx={{color: color || 'text.primary', lineHeight: 1.1}}
            >
                {value}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {label}
            </Typography>
        </Paper>
    );
    const wrapper = (
        <Box sx={{flex: '1 1 140px', minWidth: {xs: '100%', sm: 130}}}>
            {card}
        </Box>
    );
    if (!tooltip) return wrapper;
    return (
        <Tooltip title={tooltip} arrow placement="top">
            {wrapper}
        </Tooltip>
    );
}

function CoverageBucketChart({title, icrBuckets, eeiBuckets, onEntityClick}) {
    const data = useMemo(() => [
        {
            name: 'ICR',
            ...normalizeBuckets(icrBuckets),
        },
        {
            name: 'EEI',
            ...normalizeBuckets(eeiBuckets),
        },
    ], [icrBuckets, eeiBuckets]);

    return (
        <WidgetCard title={title}>
            <Box sx={{width: '100%', height: {xs: 160, sm: 220}}}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={data}
                        layout="vertical"
                        margin={{top: 4, right: 12, left: 4, bottom: 4}}
                        onClick={(state) => {
                            const name = state?.activeLabel;
                            if (name === 'ICR') onEntityClick?.('icr');
                            if (name === 'EEI') onEntityClick?.('eei');
                        }}
                        style={{cursor: 'pointer'}}
                    >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false}/>
                        <XAxis type="number" allowDecimals={false} tick={{fontSize: 11}}/>
                        <YAxis type="category" dataKey="name" width={36} tick={{fontSize: 11}}/>
                        <RechartsTooltip/>
                        <Legend wrapperStyle={{fontSize: 11}}/>
                        {BUCKET_KEYS.map((key) => (
                            <Bar
                                key={key}
                                dataKey={key}
                                name={BUCKET_LABELS[key]}
                                stackId="a"
                                fill={BUCKET_COLORS[key]}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </WidgetCard>
    );
}

function CollectionTasksByPriorityChart({byPriority}) {
    const data = useMemo(() => {
        if (!byPriority || typeof byPriority !== 'object') return [];
        return CT_PRIORITY_ORDER
            .map((priority) => {
                const statusCounts = byPriority[priority] || {};
                let total = 0;
                const statusBreakdown = {};
                for (const s of CT_STATUS_ORDER) {
                    const n = Number(statusCounts[s]) || 0;
                    statusBreakdown[s] = n;
                    total += n;
                }
                return {name: priority, total, ...statusBreakdown};
            })
            .filter((row) => row.total > 0);
    }, [byPriority]);

    return (
        <WidgetCard title="Collection tasks by status and priority">
            {data.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No collection tasks in scope.</Typography>
            ) : (
                <Box sx={{width: '100%', height: {xs: 160, sm: 220}}}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={data}
                            layout="vertical"
                            margin={{top: 4, right: 12, left: 4, bottom: 4}}
                        >
                            <CartesianGrid strokeDasharray="3 3" horizontal={false}/>
                            <XAxis type="number" allowDecimals={false} tick={{fontSize: 11}}/>
                            <YAxis type="category" dataKey="name" width={60} tick={{fontSize: 11}}/>
                            <RechartsTooltip/>
                            <Legend wrapperStyle={{fontSize: 11}}/>
                            {CT_STATUS_ORDER.map((key) => (
                                <Bar
                                    key={key}
                                    dataKey={key}
                                    name={key.replace(/_/g, ' ')}
                                    stackId="a"
                                    fill={CT_STATUS_COLORS[key]}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </WidgetCard>
    );
}

function RankedDualBars({
    title,
    rows,
    nameKey,
    dataKeyA,
    dataKeyB,
    labelA,
    labelB,
    onRowClick,
    emptyMessage,
}) {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const yAxisWidth = isSmall ? 70 : 110;

    const chartData = useMemo(() => {
        const list = Array.isArray(rows) ? [...rows] : [];
        list.sort((a, b) => {
            const aScore = Math.max(Number(a?.[dataKeyA]) || 0, Number(a?.[dataKeyB]) || 0);
            const bScore = Math.max(Number(b?.[dataKeyA]) || 0, Number(b?.[dataKeyB]) || 0);
            return bScore - aScore;
        });
        return list.slice(0, TOP_N).map((row) => ({
            name: String(row?.[nameKey] || '—'),
            [dataKeyA]: row?.[dataKeyA] == null ? 0 : Number(row[dataKeyA]) || 0,
            [dataKeyB]: row?.[dataKeyB] == null ? 0 : Number(row[dataKeyB]) || 0,
            raw: row,
        }));
    }, [rows, nameKey, dataKeyA, dataKeyB]);

    const rowHeight = isSmall ? 28 : 36;

    return (
        <WidgetCard title={title}>
            {chartData.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{emptyMessage}</Typography>
            ) : (
                <Box sx={{width: '100%', height: Math.max(isSmall ? 140 : 180, chartData.length * rowHeight)}}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={chartData}
                            layout="vertical"
                            margin={{top: 4, right: 12, left: 4, bottom: 4}}
                            onClick={(state) => {
                                const idx = state?.activeTooltipIndex;
                                if (idx == null || !chartData[idx]) return;
                                onRowClick?.(chartData[idx].raw, chartData[idx].name);
                            }}
                            style={{cursor: onRowClick ? 'pointer' : 'default'}}
                        >
                            <CartesianGrid strokeDasharray="3 3" horizontal={false}/>
                            <XAxis type="number" domain={[0, 100]} tick={{fontSize: 11}}/>
                            <YAxis
                                type="category"
                                dataKey="name"
                                width={yAxisWidth}
                                tick={{fontSize: 11}}
                            />
                            <RechartsTooltip/>
                            <Legend wrapperStyle={{fontSize: 11}}/>
                            <Bar dataKey={dataKeyA} name={labelA} fill="#42A5F5" barSize={8}/>
                            <Bar dataKey={dataKeyB} name={labelB} fill="#AB47BC" barSize={8}/>
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </WidgetCard>
    );
}

function TopicCoverageBars({rows, onTopicClick}) {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const yAxisWidth = isSmall ? 70 : 110;
    const rowHeight = isSmall ? 28 : 36;

    const chartData = useMemo(() => {
        const list = (Array.isArray(rows) ? rows : []).map((row) => {
            const dataCovered = Number(row?.data?.coveredCount) || 0;
            const dataUncovered = Number(row?.data?.uncoveredCount) || 0;
            const analyticCovered = Number(row?.analytic?.coveredCount) || 0;
            const analyticUncovered = Number(row?.analytic?.uncoveredCount) || 0;
            const dataTotal = dataCovered + dataUncovered;
            const analyticTotal = analyticCovered + analyticUncovered;
            return {
                name: String(row?.topic || '—'),
                dataPct: dataTotal ? Math.round((dataCovered / dataTotal) * 100) : 0,
                analyticPct: analyticTotal ? Math.round((analyticCovered / analyticTotal) * 100) : 0,
                raw: row,
            };
        });
        list.sort((a, b) => Math.max(b.dataPct, b.analyticPct) - Math.max(a.dataPct, a.analyticPct));
        return list.slice(0, TOP_N);
    }, [rows]);

    return (
        <WidgetCard title="Coverage per topic">
            {chartData.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No topic coverage yet (topics populate after ACC/CCC recompute).
                </Typography>
            ) : (
                <Box sx={{width: '100%', height: Math.max(isSmall ? 140 : 180, chartData.length * rowHeight)}}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={chartData}
                            layout="vertical"
                            margin={{top: 4, right: 12, left: 4, bottom: 4}}
                            onClick={(state) => {
                                const idx = state?.activeTooltipIndex;
                                if (idx == null || !chartData[idx]) return;
                                onTopicClick?.(chartData[idx].name);
                            }}
                            style={{cursor: 'pointer'}}
                        >
                            <CartesianGrid strokeDasharray="3 3" horizontal={false}/>
                            <XAxis type="number" domain={[0, 100]} tick={{fontSize: 11}}/>
                            <YAxis type="category" dataKey="name" width={yAxisWidth} tick={{fontSize: 11}}/>
                            <RechartsTooltip/>
                            <Legend wrapperStyle={{fontSize: 11}}/>
                            <Bar dataKey="dataPct" name="Data %" fill="#42A5F5" barSize={8}/>
                            <Bar dataKey="analyticPct" name="Analytic %" fill="#AB47BC" barSize={8}/>
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </WidgetCard>
    );
}

function NeedsAttentionList({items, onOpen}) {
    const list = Array.isArray(items) ? items.slice(0, 10) : [];
    return (
        <WidgetCard title="Needs attention">
            {list.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No low-coverage requirements in scope.
                </Typography>
            ) : (
                <Stack spacing={0.75} divider={undefined}>
                    {list.map((item) => {
                        const type = String(item?.type || 'icr').toUpperCase();
                        const typeColor = TYPE_COLORS[type === 'EEI' ? 'EEI' : 'ICR'];
                        const dataPct = item?.dataPct;
                        const analyticPct = item?.analyticPct;
                        return (
                            <Stack
                                key={`${item.type}:${item.id || item.number}`}
                                direction="row"
                                spacing={1}
                                alignItems="flex-start"
                                sx={{
                                    py: 0.5,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    '&:last-child': {borderBottom: 0},
                                }}
                            >
                                <Tooltip
                                    title={type === 'EEI' ? 'Essential Elements of Information' : 'Intelligence Collection Requirement'}
                                    arrow
                                    placement="top"
                                >
                                    <Chip
                                        size="small"
                                        label={type === 'EEI' ? 'EEI' : 'ICR'}
                                        sx={{
                                            height: 18,
                                            flexShrink: 0,
                                            fontWeight: 700,
                                            fontSize: '0.65rem',
                                            bgcolor: `${typeColor}22`,
                                            color: typeColor,
                                            '& .MuiChip-label': {px: 0.6},
                                        }}
                                    />
                                </Tooltip>
                                <Box sx={{minWidth: 0, flex: 1}}>
                                    <Link
                                        component="button"
                                        type="button"
                                        underline="hover"
                                        variant="body2"
                                        fontWeight={600}
                                        onClick={() => onOpen?.(item)}
                                        sx={{textAlign: 'left'}}
                                    >
                                        {item.number || item.id || '—'}
                                    </Link>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {item.question || '—'}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1} flexShrink={0}>
                                    <Typography
                                        variant="caption"
                                        fontFamily='"Roboto Mono", monospace'
                                        fontWeight={700}
                                        sx={{color: dataPct == null ? 'text.disabled' : coverageScoreColor(dataPct)}}
                                    >
                                        D {dataPct == null ? '—' : `${Math.round(dataPct)}%`}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        fontFamily='"Roboto Mono", monospace'
                                        fontWeight={700}
                                        sx={{
                                            color: analyticPct == null
                                                ? 'text.disabled'
                                                : coverageScoreColor(analyticPct),
                                        }}
                                    >
                                        A {analyticPct == null ? '—' : `${Math.round(analyticPct)}%`}
                                    </Typography>
                                </Stack>
                            </Stack>
                        );
                    })}
                </Stack>
            )}
        </WidgetCard>
    );
}

/**
 * Requirements Overview dashboard: Owner + Tags filters and aggregation widgets.
 */
export default function RequirementsOverviewPanel({
    owners,
    tags,
    onOwnersChange,
    onTagsChange,
    onSwitchTab,
}) {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [unavailable, setUnavailable] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        setUnavailable(false);
        try {
            const next = await fetchRequirementsOverview({owners, tags});
            if (next == null) {
                setData(null);
                setUnavailable(true);
            } else {
                setData(next);
            }
        } catch (e) {
            setError(e?.message || 'Failed to load overview');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [owners, tags]);

    useEffect(() => {
        load();
    }, [load]);

    const openItem = useCallback((item) => {
        if (!item) return;
        const key = item.number || item.id;
        if (!key) return;
        const type = String(item.type || '').toLowerCase();
        if (type === 'eei') {
            navigate(`/icr/eei/${encodeURIComponent(key)}`);
            return;
        }
        navigate(`/icr/${encodeURIComponent(key)}`);
    }, [navigate]);

    const handleTagClick = useCallback((tagName) => {
        const t = String(tagName || '').trim();
        if (!t) return;
        if (tags.includes(t)) return;
        onTagsChange([...(tags || []), t]);
    }, [tags, onTagsChange]);

    const totals = data?.totals || {icrs: 0, eeis: 0};
    const due = data?.due || {overdue: 0, dueSoon7d: 0};
    const freshness = data?.coverageFreshness || {computing: 0, missing: 0};

    const tagRows = useMemo(() => (data?.coverageByTag || []).map((row) => ({
        tag: row.tag,
        avgDataPct: row.avgDataPct,
        avgAnalyticPct: row.avgAnalyticPct,
        entityCount: row.entityCount,
    })), [data]);

    const handleRefresh = useCallback(() => {
        load();
    }, [load]);

    return (
        <Stack spacing={2}>
            <Stack
                direction={{xs: 'column', sm: 'row'}}
                alignItems={{xs: 'stretch', sm: 'flex-start'}}
                justifyContent="space-between"
                spacing={1}
            >
                <Box sx={{flex: 1, minWidth: 0}}>
                    <RequirementsOwnerTagsFilters
                        owners={owners}
                        tags={tags}
                        onOwnersChange={onOwnersChange}
                        onTagsChange={onTagsChange}
                        ownerLabel="Owner"
                    />
                </Box>
                <Tooltip title="Reload KPIs and charts from the same coverage data as the ICR/EEI list">
                    <span>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={loading
                                ? <CircularProgress size={14} color="inherit"/>
                                : <RefreshIcon fontSize="small"/>}
                            onClick={handleRefresh}
                            disabled={loading}
                            aria-busy={loading}
                            sx={{flexShrink: 0, mt: {xs: 0, sm: 0.5}}}
                        >
                            Refresh
                        </Button>
                    </span>
                </Tooltip>
            </Stack>

            {loading && <LinearProgress/>}

            {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {unavailable && !loading && (
                <Alert severity="info">
                    Overview data is not available yet. The aggregation API may still be deploying —
                    try again shortly, or switch to ICR / EEI tabs.
                </Alert>
            )}

            {!unavailable && !error && (
                <>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                        <KpiCard
                            label="Total ICRs"
                            value={loading && !data ? '—' : (totals.icrs ?? 0)}
                            color={TYPE_COLORS.ICR}
                            onClick={() => onSwitchTab?.('icr')}
                            tooltip="Intelligence Collection Requirements"
                        />
                        <KpiCard
                            label="Total EEIs"
                            value={loading && !data ? '—' : (totals.eeis ?? 0)}
                            color={TYPE_COLORS.EEI}
                            onClick={() => onSwitchTab?.('eei')}
                            tooltip="Essential Elements of Information"
                        />
                        <KpiCard
                            label="Overdue"
                            value={loading && !data ? '—' : (due.overdue ?? 0)}
                            color="#DC2626"
                        />
                        <KpiCard
                            label="Due in 7 days"
                            value={loading && !data ? '—' : (due.dueSoon7d ?? 0)}
                            color="#EA580C"
                        />
                        <KpiCard
                            label="Coverage computing"
                            value={loading && !data ? '—' : (freshness.computing ?? 0)}
                        />
                        <KpiCard
                            label="Coverage missing"
                            value={loading && !data ? '—' : (freshness.missing ?? 0)}
                        />
                    </Stack>

                    {data && (
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 1.5,
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    md: '1fr 1fr',
                                },
                                '& > *': {minWidth: 0},
                            }}
                        >
                            <CoverageBucketChart
                                title="Data coverage"
                                icrBuckets={data.dataCoverage?.icr}
                                eeiBuckets={data.dataCoverage?.eei}
                                onEntityClick={onSwitchTab}
                            />
                            <CoverageBucketChart
                                title="Analytic coverage"
                                icrBuckets={data.analyticCoverage?.icr}
                                eeiBuckets={data.analyticCoverage?.eei}
                                onEntityClick={onSwitchTab}
                            />
                            <CollectionTasksByPriorityChart byPriority={data.collectionTasksByPriority}/>
                            <NeedsAttentionList items={data.needsAttention} onOpen={openItem}/>
                            <RankedDualBars
                                title="Coverage per tag"
                                rows={tagRows}
                                nameKey="tag"
                                dataKeyA="avgDataPct"
                                dataKeyB="avgAnalyticPct"
                                labelA="Data %"
                                labelB="Analytic %"
                                emptyMessage="No tagged requirements in scope."
                                onRowClick={(row, name) => handleTagClick(name || row?.tag)}
                            />
                            <TopicCoverageBars
                                rows={data.coverageByTopic}
                                onTopicClick={handleTagClick}
                            />
                        </Box>
                    )}

                    {!loading && !data && !unavailable && (
                        <Box py={4}>
                            <Typography color="text.secondary" align="center">
                                No overview data for these filters.
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </Stack>
    );
}
